const loadImage = async file => {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch (error) {
      // Some browsers expose createImageBitmap but do not support imageOrientation for all camera formats.
    }
  }

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("No se pudo leer esta imagen."));
    };
    image.src = url;
  });
};

const imageWidth = image => image.width || image.naturalWidth;
const imageHeight = image => image.height || image.naturalHeight;

export const optimizeCardPhoto = async (file, {
  maxDimension = 1400,
  quality = 0.84
} = {}) => {
  if (!file) return null;
  if (!file.type.startsWith("image/")) throw new Error("Selecciona una imagen valida.");

  const image = await loadImage(file);
  const largestDimension = Math.max(imageWidth(image), imageHeight(image));
  const scale = Math.min(1, maxDimension / largestDimension);
  const width = Math.max(1, Math.round(imageWidth(image) * scale));
  const height = Math.max(1, Math.round(imageHeight(image) * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d", { alpha: false });
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, width, height);
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(image, 0, 0, width, height);

  const blob = await new Promise(resolve => canvas.toBlob(resolve, "image/webp", quality));
  if (!blob) throw new Error("No se pudo optimizar esta imagen.");

  const baseName = file.name.replace(/\.[^.]+$/, "") || "carta";
  return new File([blob], `${baseName}.webp`, {
    type: "image/webp",
    lastModified: Date.now()
  });
};


export const adjustCardPhoto = async (file, {
  rotation = 0,
  zoom = 1,
  offsetX = 0,
  offsetY = 0,
  quality = 0.86,
  width = 900,
  height = Math.round(900 * 88 / 63),
  frameInset = 0
} = {}) => {
  if (!file) return null;
  if (!file.type.startsWith("image/")) throw new Error("Selecciona una imagen valida.");

  const image = await loadImage(file);
  const radians = (Number(rotation) || 0) * Math.PI / 180;
  const sin = Math.abs(Math.sin(radians));
  const cos = Math.abs(Math.cos(radians));
  const rotatedWidth = imageWidth(image) * cos + imageHeight(image) * sin;
  const rotatedHeight = imageWidth(image) * sin + imageHeight(image) * cos;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d", { alpha: false });
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, width, height);
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";

  // The visual frame has an inset. Scale the source so the exported card is the framed area, not the whole camera photo.
  const frameCropScale = 1 / Math.max(0.2, 1 - (Number(frameInset) || 0) * 2);
  const coverScale = Math.max(width / rotatedWidth, height / rotatedHeight) * frameCropScale * Math.max(1, Number(zoom) || 1);
  const drawWidth = imageWidth(image) * coverScale;
  const drawHeight = imageHeight(image) * coverScale;
  const transformedWidth = rotatedWidth * coverScale;
  const transformedHeight = rotatedHeight * coverScale;
  const maxOffsetX = Math.max(0, (transformedWidth - width) / 2);
  const maxOffsetY = Math.max(0, (transformedHeight - height) / 2);
  const translateX = (Number(offsetX) || 0) * maxOffsetX;
  const translateY = (Number(offsetY) || 0) * maxOffsetY;

  context.save();
  context.translate(width / 2, height / 2);
  context.translate(translateX, translateY);
  context.rotate(radians);
  context.drawImage(image, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
  context.restore();

  const blob = await new Promise(resolve => canvas.toBlob(resolve, "image/webp", quality));
  if (!blob) throw new Error("No se pudo ajustar esta imagen.");

  const baseName = file.name.replace(/\.[^.]+$/, "") || "carta";
  return new File([blob], `${baseName}-ajustada.webp`, {
    type: "image/webp",
    lastModified: Date.now()
  });
};
